"use client"

import { motion } from "motion/react"
import type { Variants } from "motion/react"
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type ReactNode,
  type Ref,
} from "react"
import { useMotionUITheme, useMotionUITransition } from "@/components/motion-ui/ui-theme"
import { cn } from "@/lib/utils"

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

/** Variant label the arrow animates between. */
export const ARROW_NUDGE_VARIANT = "nudge"

export const ARROW_NUDGE_PARENT_VARIANTS: Variants = {
  rest: {},
  [ARROW_NUDGE_VARIANT]: {},
}

const ArrowNudgeActiveContext = createContext(false)

function isFocusVisibleTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && target.matches(":focus-visible")
}

/** Hover/focus wiring for a block that drives descendant `ArrowNudge` instances. */
export function useArrowNudgeActive() {
  const { motionMode } = useMotionUITheme()
  const motionAllowed = motionMode === "full"
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const active = motionAllowed && (hovered || focused)

  const gestureProps = useMemo(() => {
    if (!motionAllowed) return {}
    return {
      onHoverStart: () => setHovered(true),
      onHoverEnd: () => setHovered(false),
      onFocus: (event: FocusEvent<HTMLElement>) => {
        if (isFocusVisibleTarget(event.currentTarget)) setFocused(true)
      },
      onBlur: () => setFocused(false),
    }
  }, [motionAllowed])

  return { active, gestureProps }
}

/** Supplies `active` to descendant `ArrowNudge` instances. */
export function ArrowNudgeActiveProvider({
  active,
  children,
}: {
  active: boolean
  children: ReactNode
}) {
  return (
    <ArrowNudgeActiveContext.Provider value={active}>
      {children}
    </ArrowNudgeActiveContext.Provider>
  )
}

export interface ArrowIconProps {
  /** The glyph's width and height in px. Defaults to `16`. */
  size?: number
  /** Merged onto the `<svg>`. */
  className?: string
}

/** Bare right-arrow glyph in `currentColor`. */
export function ArrowIcon({ size = 16, className }: ArrowIconProps): ReactNode {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  )
}

export interface ArrowNudgeProps {
  /** Arrow glyph size in px. Defaults to `16`. */
  size?: number
  /** Merged onto the wrapping `motion.span`. */
  className?: string
  /** Explicit active override. Omit to read from `ArrowNudgeSurface`. */
  active?: boolean
}

/** Trailing arrow nudge for composing inside a larger interactive element. */
export function ArrowNudge({
  size,
  className,
  active: activeProp,
}: ArrowNudgeProps): ReactNode {
  const snap = useMotionUITransition("snap")
  const uiTheme = useMotionUITheme()
  const motionAllowed = uiTheme.motionMode === "full"
  const inheritedActive = useContext(ArrowNudgeActiveContext)
  const active = activeProp ?? inheritedActive
  const variants = useMemo<Variants>(
    () => ({
      rest: { transform: "translateX(0px)" },
      [ARROW_NUDGE_VARIANT]: {
        transform: `translateX(${
          motionAllowed ? uiTheme.travel.hover : 0
        }px)`,
      },
    }),
    [motionAllowed, uiTheme.travel.hover]
  )
  return (
    <motion.span
      className={cn("inline-flex", className)}
      variants={variants}
      initial="rest"
      animate={active ? ARROW_NUDGE_VARIANT : "rest"}
      transition={{ ...snap }}
      aria-hidden="true"
    >
      <ArrowIcon size={size} />
    </motion.span>
  )
}

export type ArrowNudgeSurfaceProps = ComponentPropsWithoutRef<typeof motion.a>

/** `motion.a` wrapper that drives every `ArrowNudge` inside on hover and focus. */
export function ArrowNudgeSurface({
  children,
  ...props
}: ArrowNudgeSurfaceProps): ReactNode {
  const { active, gestureProps } = useArrowNudgeActive()
  return (
    <ArrowNudgeActiveProvider active={active}>
      <motion.a {...props} {...gestureProps}>
        {children}
      </motion.a>
    </ArrowNudgeActiveProvider>
  )
}

/** Semantic colour the ghost link reads. */
export type ArrowLinkTone = "muted" | "primary"

const TONE_CLASSES: Record<ArrowLinkTone, string> = {
  muted:
    "text-muted-foreground hover:text-foreground focus-visible:text-foreground",
  primary: "text-primary hover:text-primary/80 focus-visible:text-primary/80",
}

export interface ArrowLinkProps {
  /** The link label. */
  children?: ReactNode
  /** The destination. */
  href?: string
  /** Which semantic colour the link reads. Defaults to `muted`. */
  tone?: ArrowLinkTone
  /** Anchor `target`. */
  target?: string
  /** Anchor `rel`. */
  rel?: string
  /** Click handler. */
  onClick?: () => void
  /** Merged onto the `<a>`. */
  className?: string
  /** Ref to the underlying anchor. */
  ref?: Ref<HTMLAnchorElement>
}

/** Self-contained ghost link with trailing arrow nudge. */
export function ArrowLink({
  children,
  href,
  tone = "muted",
  target,
  rel,
  onClick,
  className,
  ref,
}: ArrowLinkProps): ReactNode {
  const { active, gestureProps } = useArrowNudgeActive()
  return (
    <ArrowNudgeActiveProvider active={active}>
      <motion.a
        ref={ref}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-sm text-sm font-medium transition-colors duration-200",
          TONE_CLASSES[tone],
          FOCUS_RING,
          className
        )}
        {...gestureProps}
      >
        {children}
        <ArrowNudge />
      </motion.a>
    </ArrowNudgeActiveProvider>
  )
}
